import React, { createElement } from 'react';

import { Box, Flex, Text, VStack } from 'quotient';

import { NavigationItemComponent } from './NavigationItemComponent';
import { NavigationItem, NavigationItemType } from './types';
import { hasEmptyState, hasSubComponent, hasSubItems } from './utils/navigation-utils';

const getSubSectionTitle = (item: NavigationItemType): string | undefined => {
  if ('subSectionTitle' in item) {
    return item.subSectionTitle;
  }
  return undefined;
};

const getSubSectionTitleComponent = (item: NavigationItemType): React.ComponentType<any> | undefined => {
  if ('subSectionTitleComponent' in item) {
    return item.subSectionTitleComponent;
  }
  return undefined;
};

interface NavigationSecondarySectionProps {
  selectedItem: NavigationItemType | null;
  secondaryItems: NavigationItem[];
  selectedSecondaryItem: string | null;
  onSubItemClick: (subItem: NavigationItem) => void;
}

export const NavigationSecondarySection: React.FC<NavigationSecondarySectionProps> = ({
  secondaryItems,
  selectedItem,
  selectedSecondaryItem,
  onSubItemClick,
}) => {
  if (!selectedItem) return null;
  const subSectionTitle = getSubSectionTitle(selectedItem);
  const SubSectionTitleComponent = getSubSectionTitleComponent(selectedItem);
  const hasTitle = subSectionTitle || SubSectionTitleComponent;
  const hasSubItemsToRender = hasSubItems(selectedItem);
  return (
    <Flex className="navigation-secondary-section" direction="column" flex="1" minH="0" overflowY="auto">
      {hasTitle && (
        <Flex align="center" direction="row" justify="space-between" m={0} pb={2} pt={4}>
          {subSectionTitle && (
            <Text color="primaryNeutral.900" textStyle="headingSmall">
              {subSectionTitle}
            </Text>
          )}
          {SubSectionTitleComponent && <SubSectionTitleComponent />}
        </Flex>
      )}
      {/* Render empty state if the selected item has an empty state */}
      {!hasSubItemsToRender && hasEmptyState(selectedItem) && selectedItem.emptyState && (
        <Box>
          {/* The component could either be a function or an element */}
          {typeof selectedItem.emptyState === 'function'
            ? createElement(selectedItem.emptyState, selectedItem.emptyStateProps || {})
            : selectedItem.emptyState}
        </Box>
      )}

      {/* Render subItems if the selected item has them */}
      {hasSubItemsToRender && (
        <VStack align="stretch" spacing={0}>
          {secondaryItems
            .filter((item) => !item.hidden)
            .map((item) => (
              <NavigationItemComponent
                isSelected={selectedSecondaryItem === item.id}
                item={item}
                key={item.id}
                onClick={onSubItemClick}
              />
            ))}
        </VStack>
      )}
      {/* Render subComponent if the selected item has one */}
      {hasSubComponent(selectedItem) &&
        selectedItem.subComponent &&
        (typeof selectedItem.subComponent === 'function'
          ? createElement(selectedItem.subComponent, selectedItem.subComponentProps || {})
          : selectedItem.subComponent)}
    </Flex>
  );
};
